// Tiny client-side hook around /api/admin/me — returns whether the
// signed-in user has the admin flag. The endpoint is unauthenticated-
// safe (returns {is_admin: false, signed_in: false} when no session),
// so it's safe to call from any mount point.
//
// The result is cached for the lifetime of the page — admin status
// doesn't change mid-session in any flow we have today. If we add a
// "promote me" admin button later, the consumer can call refetch().

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/auth-fetch';

interface AdminMeResponse {
  is_admin: boolean;
  signed_in: boolean;
  user_id?: string;
}

let _cache: AdminMeResponse | null = null;
let _inflight: Promise<AdminMeResponse> | null = null;

async function fetchAdminMe(): Promise<AdminMeResponse> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await apiFetch('/api/admin/me');
      if (!res.ok) {
        const fallback: AdminMeResponse = { is_admin: false, signed_in: false };
        _cache = fallback;
        return fallback;
      }
      const body = (await res.json()) as AdminMeResponse;
      _cache = body;
      return body;
    } catch {
      const fallback: AdminMeResponse = { is_admin: false, signed_in: false };
      _cache = fallback;
      return fallback;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useIsAdmin(): { isAdmin: boolean; ready: boolean; refetch: () => void } {
  const [state, setState] = useState<AdminMeResponse | null>(_cache);

  useEffect(() => {
    let cancelled = false;
    fetchAdminMe().then((r) => {
      if (!cancelled) setState(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = useCallback(() => {
    _cache = null;
    fetchAdminMe().then((r) => setState(r));
  }, []);

  return {
    isAdmin: state?.is_admin === true,
    ready: state !== null,
    refetch,
  };
}
